/** Read-only executable-graph validation. Unknown serialized nodes remain editable. */
import { GraphOps, type GraphData } from '../../scenedata/behaviors/behaviorgraphdata';
import { getBlockDef } from './blockCatalog';

export interface GraphDiagnostic {
    code: string;
    field: string;
    message: string;
}

export class GraphValidationError extends Error {
    constructor(public readonly diagnostics: GraphDiagnostic[]) {
        super(diagnostics.map(d => `${d.field}: ${d.message}`).join('\n'));
        this.name = 'GraphValidationError';
    }
}

/** Validate the entry graph and reachable subgraphs, without allocating runtime blocks. */
export function validateExecutableGraph(entry: GraphData, allGraphs: GraphData[] = [entry]): GraphDiagnostic[] {
    const diagnostics: GraphDiagnostic[] = [];
    const visiting = new Set<GraphData>(), visited = new Set<GraphData>();
    const error = (code: string, field: string, message: string) => diagnostics.push({ code, field, message });
    const index = (n: number, length: number) => Number.isInteger(n) && n >= 0 && n < length;
    function walk(graph: GraphData, field: string): void {
        if (visiting.has(graph)) { error('SUBGRAPH_CYCLE', field, 'Recursive subgraph execution is unsupported.'); return; }
        if (visited.has(graph)) return;
        if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.declarations)
            || !Array.isArray(graph.types) || !Array.isArray(graph.variables)) {
            error('GRAPH_SHAPE', field, 'Expected nodes, declarations, types and variables arrays.'); return;
        }
        const record = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
        if (graph.nodes.some(n => !record(n) || (n.values !== undefined && (!record(n.values) || Object.values(n.values).some(v => !record(v))))
            || (n.flows !== undefined && (!record(n.flows) || Object.values(n.flows).some(v => !record(v)))))
            || graph.declarations.some(d => !record(d)) || graph.types.some(t => !record(t)) || graph.variables.some(v => !record(v))) {
            error('GRAPH_SHAPE', field, 'Nodes, declarations, types, variables and socket entries must be objects.'); return;
        }
        visiting.add(graph);
        const port = (ni: number, socket: string, kind: 'dataInputs' | 'dataOutputs' | 'flowInputs' | 'flowOutputs'): boolean => {
            if (!index(ni, graph.nodes.length)) return false;
            const node = graph.nodes[ni], decl = graph.declarations[node.declaration];
            if (!decl) return false;
            const ref = decl.extensions?.TRACE_subgraph;
            if (ref) {
                const sub = allGraphs[ref.graph];
                const names = { dataInputs: sub?.inputValueSockets, dataOutputs: sub?.outputValueSockets,
                    flowInputs: sub?.inputFlowSockets, flowOutputs: sub?.outputFlowSockets };
                return Object.hasOwn(names[kind] ?? {}, socket);
            }
            const def = getBlockDef(decl.op);
            if (!def || def.registration === 'macro') return false;
            if (kind === 'flowOutputs' && decl.op === GraphOps.FLOW_SEQUENCE) return /^(0|[1-9]\d*)$/.test(socket);
            return def[kind].some(p => (typeof p === 'string' ? p : p.name) === socket);
        };
        function literal(value: { type: number; value: unknown[] }, at: string, nullString = false): void {
            const type = graph.types[value.type]?.signature;
            if (!index(value.type, graph.types.length) || !type) {
                error('LITERAL_TYPE', at, `Invalid type index ${value.type}.`); return;
            }
            // A catalog `string` input may hold a null literal (a saved `undefined`): the data connection reads it
            // as the input's default. Variables and `any` inputs have no default to fall back to.
            const counts: Record<string, number> = { bool: 1, int: 1, float: 1, string: 1, color: 1,
                float2: 2, float3: 3, float4: 4, float2x2: 4, float3x3: 9, float4x4: 16 };
            const values = counts[type] > 1 && value.value?.length === 1 && Array.isArray(value.value[0]) ? value.value[0] : value.value;
            const valid = Array.isArray(values) && values.length === counts[type] && values.every(v =>
                type === 'bool' ? typeof v === 'boolean'
                    : type === 'string' ? typeof v === 'string' || (nullString && v === null)
                    : type === 'color' ? typeof v === 'string'
                    : typeof v === 'number' && Number.isFinite(v) && (type !== 'int' || Number.isInteger(v)));
            if (!valid) error('LITERAL_VALUE', at, `Expected ${type} literal with ${counts[type]} typed component(s); expressions are not literal defaults.`);
        }
        for (const [vi, variable] of graph.variables.entries()) literal(variable, `${field}.variables[${vi}]`);
        for (const [ni, node] of graph.nodes.entries()) {
            const at = `${field}.nodes[${ni}]`, decl = graph.declarations[node.declaration];
            if (!index(node.declaration, graph.declarations.length) || !decl) {
                error('DECLARATION_INDEX', at, `Invalid declaration index ${node.declaration}.`); continue;
            }
            const ref = decl.extensions?.TRACE_subgraph;
            if (ref) {
                if (ref.asset != null) error('EXTERNAL_SUBGRAPH', at, 'External subgraph assets cannot execute in the native compiler.');
                else if (!index(ref.graph, allGraphs.length)) error('SUBGRAPH_INDEX', at, `Invalid graph index ${ref.graph}.`);
                else walk(allGraphs[ref.graph], `graphs[${ref.graph}]`);
            } else {
                const def = getBlockDef(decl.op);
                if (!def || def.registration === 'macro') {
                    error('UNSUPPORTED_OP', at, `Unsupported native operation "${decl.op}". Use the block catalog; lower authoring macros before compilation.`);
                    continue;
                }
            }
            for (const [socket, value] of Object.entries(node.values ?? {})) {
                const atValue = `${at}.values.${socket}`;
                if (!port(ni, socket, 'dataInputs')) error('INPUT_SOCKET', atValue, `Unknown input "${socket}" on ${decl.op}.`);
                if ('node' in value) {
                    if (!port(value.node, value.socket, 'dataOutputs')) error('OUTPUT_SOCKET', atValue, `Invalid data source ${value.node}.${value.socket}.`);
                } else literal(value, atValue, !decl.extensions?.TRACE_subgraph
                    && getBlockDef(decl.op)?.dataInputs.find(p => p.name === socket)?.type === 'string');
            }
            for (const [socket, target] of Object.entries(node.flows ?? {})) {
                if (!port(ni, socket, 'flowOutputs')) error('FLOW_OUTPUT', `${at}.flows.${socket}`, `Unknown flow output "${socket}" on ${decl.op}.`);
                if (!port(target.node, target.socket, 'flowInputs')) error('FLOW_INPUT', `${at}.flows.${socket}`, `Invalid flow target ${target.node}.${target.socket}.`);
            }
            if (decl.op === GraphOps.VARIABLE_GET || decl.op === GraphOps.VARIABLE_SET) {
                const variable = node.configuration?.variable?.value?.[0] ?? 0;
                if (typeof variable !== 'number' || !index(variable, graph.variables.length)) error('VARIABLE_INDEX', at, `Invalid variable index ${JSON.stringify(variable)}.`);
            }
            if (decl.op === GraphOps.EVENT_SEND || decl.op === GraphOps.EVENT_RECEIVE) {
                // The spec gives these ops no default configuration: `event` is required.
                const event = node.configuration?.event?.value?.[0];
                const events = Array.isArray(graph.events) ? graph.events : [];
                if (typeof event !== 'number' || !index(event, events.length)) error('EVENT_INDEX', at, `Invalid custom event index ${JSON.stringify(event)}.`);
                else if (Object.keys(events[event]?.values ?? {}).length > 0) error('EVENT_VALUES', at, 'Custom events with value sockets are not supported by the native compiler yet.');
            }
        }
        for (const [name, ref] of Object.entries(graph.inputFlowSockets ?? {})) {
            if (!port(ref.node, ref.socket, 'flowInputs')) error('SUBGRAPH_INTERFACE', `${field}.inputFlowSockets.${name}`, 'Invalid internal flow input.');
        }
        for (const [name, ref] of Object.entries(graph.outputFlowSockets ?? {})) {
            if (!port(ref.node, ref.socket, 'flowOutputs')) error('SUBGRAPH_INTERFACE', `${field}.outputFlowSockets.${name}`, 'Invalid internal flow output.');
        }
        for (const [name, ref] of Object.entries(graph.outputValueSockets ?? {})) {
            if (!port(ref.node, ref.socket, 'dataOutputs')) error('SUBGRAPH_INTERFACE', `${field}.outputValueSockets.${name}`, 'Invalid internal data output.');
        }
        // Flow cycles can be deliberate delay loops. Pull-data cycles cannot
        // produce a value and otherwise recurse until the runtime stack overflows.
        const pulling = new Set<number>(), pulled = new Set<number>();
        function visitData(ni: number): void {
            if (!index(ni, graph.nodes.length) || pulled.has(ni)) return;
            if (pulling.has(ni)) { error('DATA_CYCLE', `${field}.nodes[${ni}]`, 'Cyclic data dependency; use a variable to carry state between events.'); return; }
            pulling.add(ni);
            for (const value of Object.values(graph.nodes[ni].values ?? {})) if ('node' in value) visitData(value.node);
            pulling.delete(ni); pulled.add(ni);
        }
        graph.nodes.forEach((_, ni) => visitData(ni));
        visiting.delete(graph); visited.add(graph);
    }
    walk(entry, `graphs[${Math.max(0, allGraphs.indexOf(entry))}]`);
    return diagnostics;
}

export function assertExecutableGraph(graph: GraphData, allGraphs?: GraphData[]): void {
    const diagnostics = validateExecutableGraph(graph, allGraphs);
    if (diagnostics.length) throw new GraphValidationError(diagnostics);
}
